import { useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { Feld, Fehler } from '../../components/ui';
import { useCaseAutosave, useEntityActions, dateInput } from '../../hooks';
import type { StepProps } from '../CasePage';

const PARTY_ROLES = ['AUFTRAGGEBER', 'UNTERNEHMER', 'PLANER', 'VERKAEUFER', 'KAEUFER', 'SONSTIGE'];

/** Schritt 1: Mandat, Auftraggeber, Zweck, Parteien, Vertraulichkeit. */
export function Step1Fall({ caseId, data, reload, readonly }: StepProps) {
  const autosave = useCaseAutosave(caseId, reload);
  const [felder, setFelder] = useState({
    title: data.title ?? '',
    clientName: data.clientName ?? '',
    mandate: data.mandate ?? '',
    purpose: data.purpose ?? '',
    confidentiality: data.confidentiality ?? '',
    valuationDate: dateInput(data.valuationDate),
  });

  function setField(name: keyof typeof felder, value: string) {
    setFelder((f) => ({ ...f, [name]: value }));
    if (!readonly) {
      autosave({ [name]: value === '' ? undefined : value });
    }
  }

  return (
    <>
      <section className="karte">
        <h2>{t('step1.title')}</h2>
        <p className="erklaerung">{t('step1.intro')}</p>
        <div className="feld-zeile">
          <Feld label={t('cases.caseTitle')} required>
            <input
              value={felder.title}
              onChange={(e) => setField('title', e.target.value)}
              disabled={readonly}
            />
          </Feld>
          <Feld label={t('step1.client')}>
            <input
              value={felder.clientName}
              onChange={(e) => setField('clientName', e.target.value)}
              disabled={readonly}
            />
          </Feld>
        </div>
        <Feld label={t('step1.mandate')} hint={t('step1.mandateHint')}>
          <textarea
            value={felder.mandate}
            onChange={(e) => setField('mandate', e.target.value)}
            disabled={readonly}
          />
        </Feld>
        <Feld label={t('step1.purpose')}>
          <textarea
            value={felder.purpose}
            onChange={(e) => setField('purpose', e.target.value)}
            disabled={readonly}
          />
        </Feld>
        <div className="feld-zeile">
          <Feld label={t('step1.confidentiality')}>
            <input
              value={felder.confidentiality}
              onChange={(e) => setField('confidentiality', e.target.value)}
              disabled={readonly}
            />
          </Feld>
          <Feld label={t('step1.valuationDate')} hint={t('step1.valuationDateHint')}>
            <input
              type="date"
              value={felder.valuationDate}
              onChange={(e) => setField('valuationDate', e.target.value)}
              disabled={readonly}
            />
          </Feld>
        </div>
      </section>

      <ParteienKarte caseId={caseId} data={data} reload={reload} readonly={readonly} />
    </>
  );
}

function ParteienKarte({
  caseId,
  data,
  reload,
  readonly,
}: Pick<StepProps, 'caseId' | 'data' | 'reload' | 'readonly'>) {
  const actions = useEntityActions(caseId, 'parties', reload);
  const [form, setForm] = useState({ role: 'AUFTRAGGEBER', name: '', address: '', contact: '' });

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await actions.create({
      role: form.role,
      name: form.name,
      address: form.address || undefined,
      contact: form.contact || undefined,
    });
    if (ok) setForm({ role: 'AUFTRAGGEBER', name: '', address: '', contact: '' });
  }

  return (
    <section className="karte">
      <h2>{t('step1.parties')}</h2>
      {data.parties.length === 0 ? (
        <div className="leer-hinweis">{t('step1.partiesEmpty')}</div>
      ) : (
        data.parties.map((p: any) => (
          <div className="eintrag" key={p.id}>
            <div className="eintrag-kopf">
              <strong>{p.name}</strong>
              <span className="badge">{t(`party.role.${p.role}`)}</span>
              {!readonly && (
                <button
                  type="button"
                  className="knopf-sekundaer knopf-klein"
                  onClick={() => actions.remove(p.id)}
                >
                  {t('common.delete')}
                </button>
              )}
            </div>
            {(p.address || p.contact) && (
              <p className="meta">
                {p.address}
                {p.address && p.contact ? ' · ' : ''}
                {p.contact}
              </p>
            )}
          </div>
        ))
      )}
      {!readonly && (
        <form onSubmit={add}>
          <h3>{t('step1.addParty')}</h3>
          <div className="feld-zeile">
            <Feld label={t('step1.partyRole')} required>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {PARTY_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(`party.role.${r}`)}
                  </option>
                ))}
              </select>
            </Feld>
            <Feld label={t('step1.partyName')} required error={actions.fieldErrors['name']}>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Feld>
          </div>
          <div className="feld-zeile">
            <Feld label={t('step1.partyAddress')}>
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Feld>
            <Feld label={t('step1.partyContact')}>
              <input
                value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
              />
            </Feld>
          </div>
          <Fehler code={actions.errorCode} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">
              {t('common.add')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
